import { Box, Divider, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { MedicalNoteActivity, MedicalNoteType, User } from '@uplift/api';
import { useCptCodesByIdsQuery, useDiagnosesByIdsQuery, useSessionByAppointmentIdQuery, useUserDisplayNameQuery } from '@uplift/queries';
import { CalendarDateService } from '@uplift/services';
import React, { useMemo } from 'react';
import { Loader } from '../../../../../../Loader';

export type ClaimCorrectionNoteReviewProps = {
  note: MedicalNoteActivity<MedicalNoteType.claimCorrectionNote>;
  provider: Pick<User, 'firstName' | 'lastName'>;
  timezone: string;
};

const enum NoteFormEnum {
  serviceDate = 'serviceDate',
  codeChangeDate = 'codeChangeDate',
  codeChangeDescription = 'codeChangeDescription',
  otherCodeChangeReason = 'otherCodeChangeReason',
  cptCodeChangeReason = 'cptCodeChangeReason',
  diagnosisCodeChangeReason = 'diagnosisCodeChangeReason'
}

type CodeSectionProps = {
  cptCodeIds: string[];
  reason?: string;
  className?: string;
};

type DiagnosisSectionProps = {
  diagnosisIds: string[];
  reason?: string;
  className?: string;
};

type OtherSectionType = {
  description: string;
  reason: string;
};

const isWhitespace = (str: string): boolean => /^\s*$/.test(str);

const DiagnosisSection: React.VFC<DiagnosisSectionProps> = ({ diagnosisIds, reason, className }) => {
  const { data: diagnoses } = useDiagnosesByIdsQuery(diagnosisIds);
  const { classes, cx } = useStyles();

  return (
    <Box pb="8px">
      <Typography variant="body1" className={cx(classes.sectionHeader, className)}>Diagnosis</Typography>
      {diagnoses?.map(({ name, id, code }) => {
        return <Typography variant="body1" className={className} key={id}>{`${code}`}&nbsp;{name}</Typography>;
      })}
      {reason && !isWhitespace(reason) && (
        <Box display="flex" flexDirection="row" alignItems="center">
          <Typography
            variant="subtitle1"
            className={classes.reason}
          >Code Change Reason: <span className={classes.span}>{reason}</span></Typography>
        </Box>
      )}
    </Box>
  );
};

const CptCodeSection: React.VFC<CodeSectionProps> = ({ cptCodeIds, reason, className }) => {
  const { data: cptCodes } = useCptCodesByIdsQuery(cptCodeIds);
  const { classes, cx } = useStyles();
  const primary = cptCodes?.filter(x => x.codingType === 'primary');
  const addons = cptCodes?.filter(x => x.codingType !== 'primary');
  const isPlural = cptCodeIds.length > 1;
  const isPrimary = primary?.length && primary.length > 0;


  return (
    <Box pb="8px">
      <Typography variant="body1" className={cx(classes.sectionHeader, className)}>{'CPT Code'}{isPlural ? 's' : ''}</Typography>
      <Box>
        {isPrimary && <Typography variant="body1" className={className}>{`${primary[0].code}:`}&nbsp;{primary[0].name}</Typography>}
        {addons?.map(({ name, id, code }) => {
          return <Typography variant="body1" className={className} key={id}>{`+${code}:`}&nbsp;{name}</Typography>;
        })}
      </Box>
      {reason && !isWhitespace(reason) && (
        <Box display="flex" flexDirection="row" alignItems="center">
          <Typography
            variant="subtitle1"
            className={classes.reason}
          >Code Change Reason: <span className={classes.span}>{reason}</span></Typography>
        </Box>
      )}
    </Box>
  );
};

const OtherSection: React.VFC<OtherSectionType> = ({ description, reason }) => {
  const { classes, cx } = useStyles();

  if (isWhitespace(description)) {
    return null;
  }

  return (
    <Box>
      <Typography variant="body1" className={cx(classes.sectionHeader)}>Other</Typography>
      <Typography variant="body1">{description}</Typography>
      {reason && !isWhitespace(reason) && (
        <Box display="flex" flexDirection="row" alignItems="center">
          <Typography
            variant="subtitle1"
            className={classes.reason}
          >Code Change Reason: <span className={classes.span}>{reason}</span></Typography>
        </Box>
      )}
    </Box>
  );
};

export const ClaimCorrectionNoteReview: React.VFC<ClaimCorrectionNoteReviewProps> = ({ provider, note, timezone }) => {
  const { data: session, isLoading } = useSessionByAppointmentIdQuery(note?.additionalDetails?.appointmentId ?? '');
  const coderId = note.additionalDetails?.coderId;
  const { data: coderNameData } = useUserDisplayNameQuery(coderId, { enabled: !!coderId });
  const submittedAt = CalendarDateService.date(note.submittedAt).tz(timezone).format('MM/DD/YYYY, h:mm a');
  const { classes } = useStyles();
  const isSessionDiagnosis = session?.diagnosisIds && session?.diagnosisIds.length > 0;
  const isCorrectedDiagnosis = note?.diagnosisIds && note?.diagnosisIds.length > 0;
  const isCorrectedCptCodes = note?.cptCodeIds && note.cptCodeIds.length > 0;
  const isOther = note?.form[NoteFormEnum.codeChangeDescription].key;

  const sessionCptIds = useMemo(() => {
    const result = [];

    if (session?.cptCodeId) {
      result.push(session.cptCodeId);
    }
    if (session?.addonCptCodeIds) {
      result.push(...session.addonCptCodeIds);
    }
    return result;
  }, [ session?.addonCptCodeIds, session?.cptCodeId ]);


  if (isLoading) {
    return <Loader />;
  }

  return (
    <Box display="flex" gap="24px" flexDirection="column">
      <div>
        <Typography variant="overline" color="secondary">
        Date of Code Change
        </Typography>
        <Typography>
          {CalendarDateService.date(note.form[NoteFormEnum.codeChangeDate].value).format('LL')} {`by ${coderNameData?.defaultDisplay}`}
        </Typography>
      </div>
      <Divider />
      <div>
        <Typography variant="overline" color="secondary">
        Date of Service
        </Typography>
        <Typography>
          {CalendarDateService.date(note.form[NoteFormEnum.serviceDate].value).format('LL')} {`with ${provider.firstName} ${provider.lastName}`}
        </Typography>
        <Box className={classes.originalContainer}>
          <Box className={classes.originalCodeBox}>
            <Typography variant="body2" className={classes.header}>Originally coded as</Typography>
            {isSessionDiagnosis && (
              <DiagnosisSection
                diagnosisIds={session?.diagnosisIds}
                className={classes.originalSectionFont}
              />
            )}
            {sessionCptIds.length > 0 && (
              <CptCodeSection
                cptCodeIds={sessionCptIds}
                className={classes.originalSectionFont}
              />
            )}
          </Box>
        </Box>
      </div>
      <Divider />
      <div>
        <Typography variant="overline" color="secondary">
          Code changes
        </Typography>
        {isCorrectedCptCodes && (
          <CptCodeSection
            cptCodeIds={note?.cptCodeIds}
            reason={note?.form[NoteFormEnum.cptCodeChangeReason].value}
          />
        )}
        {isCorrectedDiagnosis && (
          <DiagnosisSection
            diagnosisIds={note?.diagnosisIds}
            reason={note?.form[NoteFormEnum.diagnosisCodeChangeReason].value}
          />
        )}
        {isOther && (
          <OtherSection
            description={note?.form[NoteFormEnum.codeChangeDescription].value}
            reason={note?.form[NoteFormEnum.otherCodeChangeReason].value}
          />
        )}
      </div>
      <Divider />
      <Typography>{`Signed and submitted by ${coderNameData?.full} on ${submittedAt}`}</Typography>
    </Box>
  );
};

const useStyles = makeStyles()(theme => ({
  originalCodeBox: {
    backgroundColor: theme.palette.grey[100],
    padding: theme.spacing(2),
  },
  originalContainer: {
    paddingTop: theme.spacing(2),
    paddingRight: theme.spacing(4)
  },
  header: {
    fontStyle: 'italic',
    paddingBottom: theme.spacing(1)
  },
  reason: {
    color: theme.palette.grey[600],
    paddingRight: theme.spacing(1),
    fontWeight: 500,
    fontSize: '14px'
  },
  sectionHeader: {
    fontWeight: 500
  },
  originalSectionFont: {
    fontSize: '14px'
  },
  span: {
    color: theme.palette.grey[600],
    fontWeight: 400
  }
}));
